import { Button, DatePicker, Dropdown, Popover, Space, Typography } from "antd";
import dayjs from "dayjs";
import { useState } from "react";
import { TbCalendarStats, TbX } from "react-icons/tb";
import { collapsedDateRangeText } from "utils/functions";
import { dateRanges, getSelectedDateRange, initialDateRange, T_DateRangeOptional } from "./CustomDateRange.utils";

export default function CustomDateRange({
  startDate,
  endDate,
  onChange,
}: {
  startDate: string | undefined;
  endDate: string | undefined;
  onChange: (opts: { startDate?: string | undefined; endDate?: string | undefined }) => void;
}) {
  const selectedDateRange = getSelectedDateRange(startDate, endDate);
  const [dateRange, setDateRange] = useState<T_DateRangeOptional>(selectedDateRange);

  const [isCustomDateRangePopoverOpen, setIsCustomDateRangePopoverOpen] = useState(false);
  const [isCustomStartDatePopoverOpen, setIsCustomStartDatePopoverOpen] = useState(false);
  const [isCustomEndDatePopoverOpen, setIsCustomEndDatePopoverOpen] = useState(false);

  return (
    <Space.Compact>
      <Dropdown
        trigger={["click"]}
        menu={{
          items: [
            ...dateRanges().map((el) => {
              return {
                label: el.label,
                key: el.label,
                extra: (
                  <Typography.Text type="secondary">{collapsedDateRangeText(el.startDate, el.endDate)}</Typography.Text>
                ),
                onClick: () => {
                  setDateRange(el);
                  onChange({ startDate: el.startDate, endDate: el.endDate });
                },
              };
            }),
            {
              label: "Custom",
              key: "custom",
              onClick: () => {
                setDateRange((prev) => ({ ...prev, label: "Custom" }));
                setIsCustomDateRangePopoverOpen(true);
                setIsCustomStartDatePopoverOpen(true);
              },
            },
          ],
        }}
      >
        <Button>{dateRange.label}</Button>
      </Dropdown>
      {dateRange.label === "Custom" ? (
        <Popover
          open={isCustomDateRangePopoverOpen}
          onOpenChange={(open) => setIsCustomDateRangePopoverOpen(open)}
          trigger={"click"}
          arrow={false}
          placement="bottomLeft"
          content={() => {
            return (
              <div className="flex flex-col gap-2">
                <Typography.Text>Start Date</Typography.Text>
                <DatePicker
                  allowClear={!!dateRange.endDate}
                  open={isCustomStartDatePopoverOpen}
                  onOpenChange={(open) => {
                    if (open === false) {
                      setIsCustomStartDatePopoverOpen(false);
                      setIsCustomEndDatePopoverOpen(true);
                    } else {
                      setIsCustomStartDatePopoverOpen(true);
                    }
                  }}
                  value={dateRange?.startDate ? dayjs(dateRange?.startDate) : undefined}
                  onChange={(date) => {
                    setDateRange((prev) => ({ ...prev, startDate: date ? date.toISOString() : undefined }));
                    // onClose({ startDate: date ? date.toISOString() : undefined });
                  }}
                  disabledDate={(date) => {
                    if (dateRange?.endDate) {
                      return dayjs(dateRange?.endDate).isBefore(date);
                    } else return false;
                  }}
                />
                <Typography.Text>End Date</Typography.Text>
                <DatePicker
                  allowClear={!!dateRange.startDate}
                  open={isCustomEndDatePopoverOpen}
                  onOpenChange={(open) => {
                    setIsCustomEndDatePopoverOpen(open);
                  }}
                  value={dateRange?.endDate ? dayjs(dateRange?.endDate) : undefined}
                  onChange={(date) => {
                    setDateRange((prev) => ({ ...prev, endDate: date ? date.toISOString() : undefined }));
                    onChange({ endDate: date ? date.toISOString() : undefined, startDate: dateRange.startDate });
                  }}
                  disabledDate={(date) => {
                    if (dateRange?.startDate) {
                      return dayjs(dateRange?.startDate).isAfter(date);
                    } else return false;
                  }}
                />
              </div>
            );
          }}
        >
          <Button
            className="border-gray-300"
            variant={dateRange?.startDate || dateRange?.endDate ? "filled" : "outlined"}
            color={dateRange?.startDate || dateRange?.endDate ? "primary" : "default"}
            icon={<TbCalendarStats />}
          >
            {collapsedDateRangeText(dateRange.startDate, dateRange.endDate)}
          </Button>
        </Popover>
      ) : null}
      {dateRange?.startDate === initialDateRange.startDate && dateRange?.endDate === initialDateRange.endDate ? null : (
        <>
          <Button
            icon={<TbX />}
            onClick={() => {
              setDateRange(initialDateRange);
              onChange(initialDateRange);
            }}
          />
        </>
      )}
    </Space.Compact>
  );
}
